import alt from '../alt';
import { Components, Groups } from '../config/components';
import Model from '../utils/model';
import PageActions from '../actions/page';
import { clone } from 'ramda';
import { isDescendant } from '../utils/pageContainers';

export class PageStore {

    constructor() {

        this.models = {};
        this.flows = {};
        this.flowContext = {};
        this.editorKey = {};

        this.components = Components;
        this.groups = Groups;

        this.bindListeners({
            setHighlightedElement: PageActions.setHighlightedElement,
            initializeModel: PageActions.initializeModel,
            savePage: PageActions.savePage,
            editPageData: PageActions.editPageData,
            removeContainer: PageActions.removeContainer,
            removeComponent: PageActions.removeComponent,
            onEditElement: [PageActions.editComponent, PageActions.editContainer, PageActions.editComponentDone, PageActions.editContainerDone],
            addContainer: PageActions.addContainer,
            cloneContainer: PageActions.cloneContainer,
            addComponent: PageActions.addComponent,
            changeContainerType: PageActions.changeContainerType,
            setContainer: PageActions.setContainer,
            setComponent: PageActions.setComponent,
            togglePreview: PageActions.togglePreview,
            deleteStore: PageActions.delete,
            setFlows: PageActions.setFlows,
            changePageContext: PageActions.changePageContext,
            refreshEditor: PageActions.refreshEditor,
            setPageConditions: PageActions.setPageConditions,
            setConfigHeader: PageActions.setConfigHeader,
            setEditingType: PageActions.setEditingType,
            moveItem: PageActions.moveItem,
            moveItemOrder: [PageActions.moveItemUp, PageActions.moveItemDown],
            clear: PageActions.clear,
        });
    }

    setHighlightedElement(data) {
        this.models[data.modelId].highlight = this.models[data.modelId].highlight === data.elementId ? null : data.elementId;
    }

    initializeModel(page) {

        const newPage = this.models[page.modelId] = {};

        newPage.model = Model.fromPageModel(page.model);
        newPage.metadata = page.model;
        newPage.isPreview = false;
        newPage.isMetadataVisible = false;
        newPage.isDirty = false;
        newPage.editing = {
            type: null,
            header: 'Page Layout',
            id: null,
            flow: null,
            command: null,
        };
        newPage.moving = {
            isMoving: false,
            source: null,
            target: null,
        };
        newPage.droppedOn = null;

        if (!this.editorKey[page.modelId]) {
            this.editorKey[page.modelId] = page.modelId;
        }
    }

    refreshEditor(pageId) {
        this.editorKey[pageId] = Model.guid();
    }

    savePage(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = false;

        const objectData = $.extend(true, {}, currentPage.model);
        manywho.state.setComponent(data.id, { objectData: [Model.toPageModel(objectData)] }, data.flowKey, true);

        const outcomes = manywho.model.getOutcomes(data.id, data.flowKey);
        const saveOutcome = manywho.model.getOutcome(outcomes.filter(outcome => outcome.developerName === 'Save')[0].id, data.flowKey);

        this.models[data.modelId].editing = {
            command: 'edit',
        };
        manywho.engine.move(saveOutcome, data.flowKey);
    }

    editPageData(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = true;
        currentPage.model[data.property].value = data.value;
    }

    changePageContext(data) {
        this.flowContext[data.modelId] = data.flowId;
    }

    removeContainer(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = true;
        currentPage.model.pageContainers.data = Model.removeContainer(data.containerId, currentPage.model.pageContainers.data);
        // Remove all components (orphans) no longer in a container.
        currentPage.model.pageComponents.data = currentPage.model.pageComponents.data
            .filter(component => Model.findContainer(component.pageContainerId.value, currentPage.model.pageContainers.data));
    }

    removeComponent(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = true;
        currentPage.model.pageComponents.data = currentPage.model.pageComponents.data.filter(component => component.id.value !== data.componentId);
    }

    onEditElement(element) {
        this.models[element.modelId].editing = element.editing;
        this.models[element.modelId].droppedOn = element.droppedOn || null;
        this.models[element.modelId].droppedOrder = element.droppedOrder;
    }

    togglePreview(modelId) {
        this.models[modelId].isPreview = !this.models[modelId].isPreview;
    }

    /**
     * Make a copy of the container identified by ContainerId and attach to the same parent.
     *
     * For a Page we keep a tree of Containers with an Id and ParentId to describe the hierarchy.
     * Within each Container there are a list of optional Components. These Components are stored
     * in a separate list, again with an Id and ContainerId to attach components to each container.
     *
     * Once we have cloned the Container, we need to iterate through each of the Components from the
     * source Container and clone them into the cloned Container fixing up various Ids. Just to add
     * to the complication Containers may be nested inside the Container we are cloning so we need
     * to recurse down the tree fixing all those Ids and any Components within.
     */
    cloneContainer({ containerId, modelId }) {
        const suffix = ' (copy)';
        const containers = Model.getFlatContainers(this.models[modelId].model.pageContainers.data, []);
        const sourceContainer = Model.findContainer(containerId, containers);
        const parentContainer = Model.findContainer(sourceContainer.parentContainer, containers);
        this.models[modelId].isDirty = true;


        const clonedContainerId = Model.guid();
        const clonedContainer = clone(sourceContainer);

        // Fix up Ids in clone
        clonedContainer.id.value = clonedContainerId;

        // Quick fixup of names
        clonedContainer.developerName.value += suffix;
        clonedContainer.label.value = clonedContainer.label.value ? clonedContainer.label.value += suffix : '';

        // Sort to the bottom of the parent list, taking into account any components in the container
        let numComponents = 0;
        if (this.models[modelId].model.pageComponents.data) {
            numComponents = this.models[modelId].model.pageComponents.data.filter(c => c.pageContainerId.value === parentContainer.id.value).length;
        }
        clonedContainer.order.value = parentContainer.pageContainers.data.length + numComponents;

        const cloneComponents = (oldContainerId, newContainerId, pageComponents) => {

            // Find any components in the 'original' container. Clone so we don't modify pageComponents
            if (pageComponents) {
                const components = clone(pageComponents.filter(component => component.pageContainerId.value === oldContainerId));
                components.forEach(function y(_itm, idx) {
                    this[idx].element.internalId = Model.guid();
                    this[idx].element.externalId = null;
                    this[idx].id.value = Model.guid();
                    this[idx].pageContainerId.value = newContainerId;
                }, components);

                // Now append the new cloned components to the end of the existing list
                components.map(component => pageComponents.push(component));
            }
        };

        // Update all Ids and ParentIds with new values for any child containers
        const fixContainerIds = (container, newParentId, pageComponents) => {

            if (container.pageContainers.data) {
                container.pageContainers.data.forEach(function x(_item, index) {

                    // New container ID
                    const guid = Model.guid();
                    // Copy any components into this child container
                    cloneComponents(this[index].id.value, guid, pageComponents);
                    this[index].id.value = guid;
                    this[index].parentContainer = newParentId;
                    this[index].element.internalId = Model.guid();
                    this[index].element.externalId = null;

                    // and the pesky kids
                    fixContainerIds(this[index], guid, pageComponents);
                }, container.pageContainers.data);
            }
        };

        fixContainerIds(clonedContainer, clonedContainer.id.value, this.models[modelId].model.pageComponents.data);
        // Finally copy any components in the top-level container
        cloneComponents(sourceContainer.id.value, clonedContainerId, this.models[modelId].model.pageComponents.data);

        // Add new cloned container to parent container list
        parentContainer.pageContainers.data.push(clonedContainer);
    }

    addContainer(data) {
        const containers = Model.getFlatContainers(this.models[data.modelId].model.pageContainers.data, []);
        const parent = Model.findContainer(data.parentId, containers);
        const containerId = Model.guid();

        const container = {
            element: {
                developerName: 'PageContainer',
                externalId: null,
                internalId: null,
                isSelected: 0,
                order: data.order,
            },
            id: {
                contentType: 'ContentString',
                typeElementPropertyId: '50415c87-d6e6-49ff-bdb2-573a65d3ee34',
                value: containerId,
            },
            order: {
                contentType: 'ContentNumber',
                typeElementPropertyId: 'b26b7a52-ab7e-45ab-8a07-d020ccc98b2b',
                value: data.order,
            },
            developerName: {
                contentType: 'ContentString',
                typeElementPropertyId: '4611be03-2153-44e3-921a-98dc7a2eab8b',
                value: data.name,
            },
            containerType: {
                contentType: 'ContentString',
                typeElementPropertyId: '01bc11a2-abdf-4038-a34f-8d41b56c5b13',
                value: data.type,
            },
            pageContainers: {
                contentType: 'ContentList',
                typeElementPropertyId: 'c7733597-c67f-4b9b-a976-c00795521847',
                value: null,
            },
            parentContainer: data.parentId,
        };

        const children = parent.pageContainers;

        if (children.data == null) {
            children.data = [container];
        } else {
            children.data.push(container);
        }

        this.models[data.modelId].editing = {
            type: 'CONTAINER',
            id: containerId,
            header: `${data.type}: ${data.name}`,
            flow: 'Page Container',
            command: 'new',
        };
    }

    addComponent(data) {
        const containers = Model.getFlatContainers(this.models[data.modelId].model.pageContainers.data, []);
        const newComponent = manywho.utils.getObjectDataProperty(data.response.outputs, 'Page Component');
        const parsedComponent = Model.parseObjectDataProperties(newComponent.objectData[0]);
        const container = Model.findContainer(parsedComponent.pageContainerId.value, containers);
        parsedComponent.pageContainerDeveloperName.value = container.developerName.value;
        const components = this.models[data.modelId].model.pageComponents.data;
        components.push(parsedComponent);
    }

    changeContainerType(data) {
        const containers = Model.getFlatContainers(this.models[data.modelId].model.pageContainers.data, []);
        const container = Model.findContainer(data.id, containers);

        for (const prop in container) {
            if (manywho.utils.isEqual(prop, 'containerType', true)) {
                container[prop].value = data.type;
            }
        }
    }

    setContainer(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = true;

        const containers = Model.getFlatContainers(currentPage.model.pageContainers.data, []);
        const components = currentPage.model.pageComponents.data;

        for (let component of components) {
            if (manywho.utils.isEqual(component.pageContainerId.value, manywho.utils.getObjectDataProperty(data.container.properties, 'Id').contentValue, true)) {
                component.pageContainerDeveloperName.value = manywho.utils.getObjectDataProperty(data.container.properties, 'DeveloperName').contentValue;
            }
        }

        let existingContainer = Model.findContainer(manywho.utils.getObjectDataProperty(data.container.properties, 'Id').contentValue, containers);
        const updatedContainer = Model.parseContainers([data.container], existingContainer.parentContainer);
        existingContainer = manywho.utils.extend(existingContainer, updatedContainer[0]);
    }

    setComponent(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = true;

        const containers = Model.getFlatContainers(currentPage.model.pageContainers.data, []);

        const updatedComponent = Model.parseObjectDataProperties(data.component);
        const container = Model.findContainer(updatedComponent.pageContainerId.value, containers);

        updatedComponent.pageContainerDeveloperName.value = container.developerName.value;

        currentPage.model.pageComponents.data = currentPage.model.pageComponents.data.filter(component => component.id.value !== updatedComponent.id.value);

        (container.pageContainers.data || [])
            .concat(currentPage.model.pageComponents.data.filter(component => component.pageContainerId.value === updatedComponent.pageContainerId.value))
            .forEach((item) => {
                if (parseInt(item.order.value, 10) >= parseInt(updatedComponent.order.value, 10)) {
                    item.order.value = parseInt(item.order.value, 10) + 1;
                }
            });

        currentPage.model.pageComponents.data.push(updatedComponent);
    }

    deleteStore(modelId) {
        this.models[modelId] = null;
    }

    setFlows(data) {
        this.flows[data.modelId] = data.flows.map((flow) => {
            return { label: flow.developerName, value: flow.id.id };
        });
    }

    setPageConditions(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = true;
        currentPage.model.pageConditions.data = data.pageConditions;
    }

    setConfigHeader(data) {
        this.models[data.modelId].editing.header = data.header;
    }

    setEditingType(data) {
        this.models[data.modelId].editing.type = data.type;
    }

    moveItem(data) {
        const currentPage = this.models[data.modelId];

        currentPage.isDirty = true;

        const containers = Model.getFlatContainers(currentPage.model.pageContainers.data, []);
        const components = currentPage.model.pageComponents.data;
        const pageItems = containers.concat(components);

        const item = pageItems.find(pageItem => pageItem.id.value === data.item.id);

        // Changing the parent of a container, remove the container thats being moved from its current parent
        if (data.item.containerType) {

            // Ignore moving a parent into a child/grandchild etc.
            // Notes,
            //     1. data.source is not really the id of the dropped item, it is the parent container, hence we use data.item as the dropped item
            //     2. The drag/drop code checks for isDescendant() to prevent dropping. This is here as a safety net to avoid trashing the page.
            const allContainers = Model.stripValues(currentPage.model.pageContainers.data);
            if (isDescendant(data.item.id, data.target, allContainers)) {
                return;
            }

            const currentParent = containers.find(container => container.id.value === data.source);
            currentParent.pageContainers.data = currentParent.pageContainers.data.filter(container => container.id.value !== item.id.value);
        } else if (data.item.componentType) {
            item.pageContainerId.value = null;
        }

        const newParent = containers.find(container => container.id.value === data.target);

        if (!newParent.pageContainers.data) {
            newParent.pageContainers.data = [];
        }

        let containerItems = (newParent.pageContainers.data || [])
            .concat(components.filter(component => component.pageContainerId.value === data.target))
            .sort((a, b) => parseInt(a.order.value, 10) - parseInt(b.order.value, 10));

        let targetIndex = 0;

        if (data.sibling) {
            targetIndex = Math.max(containerItems.indexOf(pageItems.find(pageItem => pageItem.id.value === data.sibling)), 0);
        }

        if (!data.isBefore) {
            targetIndex += 1;
        }

        containerItems.splice(targetIndex, 0, item);

        containerItems = containerItems.map((containerItem, index) => {
            containerItem.order.value = index;
            return containerItem;
        });

        if (item.containerType) {
            newParent.pageContainers.data.push(item);
        } else if (item.componentType) {
            item.pageContainerId.value = newParent.id.value;
            item.pageContainerDeveloperName.value = newParent.developerName.value;
        }
    }

    moveItemOrder(data) {
        const containers = Model.getFlatContainers(this.models[data.modelId].model.pageContainers.data, []);
        const components = this.models[data.modelId].model.pageComponents.data;
        const container = containers.find(acontainer => acontainer.id.value === data.container);

        const items = (container.pageContainers.data || []).concat(components.filter(component => component.pageContainerId.value === data.container))
            .sort((a, b) => parseInt(a.order.value, 10) - parseInt(b.order.value, 10));

        this.moveItem({
            modelId: data.modelId,
            item: data.item,
            source: data.container,
            target: data.container,
            sibling: items[data.order].id.value,
            isBefore: data.isBefore,
        });
    }

    clear() {
        this.models = {};
        this.flows = {};
        this.flowContext = {};
        this.editorKey = {};
    }

}

export default alt.createStore(PageStore, 'page');
